import { SUPPORTED_LANGUAGES, stripLanguagePrefix, type Language } from '@/lib/i18n'

/**
 * MATRIZ DE PUBLICAÇÃO — fonte única da verdade sobre o que o site REALMENTE
 * serve hoje, por rota e por idioma.
 *
 * Por que ela existe: o `vercel.json` represa parte da árvore pública no
 * `coming-soon.html` (decisão de negócio vigente). Anunciar `hreflang` para
 * uma URL represada é declarar ao buscador uma equivalência falsa — quem
 * acessa `/ja/diretorio` direto recebe a página de "em breve", não o
 * diretório japonês. O hook `useDocumentMeta` consome esta matriz para só
 * anunciar combinações que existem de fato.
 *
 * ────────────────────────────────────────────────────────────────────────
 * QUANDO O NEGÓCIO LIBERAR UMA ROTA, MUDA-SE UM LUGAR SÓ: mova o segmento de
 * `DAMMED_SEGMENTS` para `PUBLISHED_SEGMENTS` aqui, e troque o destino
 * correspondente no `vercel.json` de `/coming-soon.html` para `/index.html`.
 * Nenhum outro arquivo precisa ser tocado — nem o hook, nem o HTML estático
 * (o teste `publication-matrix.test.ts` prova que os dois seguem alinhados).
 * ────────────────────────────────────────────────────────────────────────
 *
 * Espelho de `vercel.json` (2026-08-21).
 */

/** Rotas servidas pelo SPA em TODOS os idiomas. `''` é a raiz de cada idioma. */
export const PUBLISHED_SEGMENTS = ['', 'inicio', 'mapa', 'forum'] as const

/** Rotas represadas no `coming-soon.html` em TODOS os idiomas. */
export const DAMMED_SEGMENTS = ['diretorio', 'inovacao', 'cadastrar', 'conexoes', 'contato'] as const

/** Primeiro segmento de um path já sem prefixo de idioma. `/forum/42` -> 'forum'. */
function segmentOf(barePathname: string): string {
  return (barePathname || '/').split('/').filter(Boolean)[0] ?? ''
}

/**
 * A rota (já sem prefixo) está publicada NESTE idioma?
 *
 * A assinatura é por (rota, idioma) de propósito, ainda que hoje o represamento
 * seja idêntico nos quatro idiomas: se amanhã o negócio abrir só `/en/diretorio`,
 * a mudança cabe aqui dentro sem alterar nenhum consumidor.
 */
export function isPublished(barePathname: string, lang: Language): boolean {
  if (!(SUPPORTED_LANGUAGES as readonly string[]).includes(lang)) return false
  const segment = segmentOf(barePathname)
  return (PUBLISHED_SEGMENTS as readonly string[]).includes(segment)
}

/**
 * Idiomas em que esta rota pode ser anunciada como alternativa.
 * Rota represada (ou desconhecida) devolve `[]` — e aí nenhum `hreflang` é
 * emitido, que é o comportamento correto: silêncio em vez de promessa falsa.
 */
export function publishedLanguages(barePathname: string): Language[] {
  return SUPPORTED_LANGUAGES.filter((lang) => isPublished(barePathname, lang))
}

/** Conveniência para chamadores que ainda têm o path COM prefixo de idioma. */
export function publishedLanguagesForPath(pathname: string): Language[] {
  return publishedLanguages(stripLanguagePrefix(pathname))
}
